// ⚠️ AMENDED 2026-10-02 (account-area redesign, Task 4; proposal a11y + D7).
// ORIGINAL: a role="tablist" of role="tab" buttons with a roving tabindex and Arrow/Home/End
// keys at wide widths, and a labelled <select> below the breakpoint.
// CURRENT: a labelled <nav> holding a <ul> of <button aria-current="page"> items in BOTH
// layouts: a vertical "list" beside the content, and a horizontal scrolling "strip" when the
// area is narrow. Why: this is page navigation, not a tab panel switcher. A <nav> with
// aria-current is the pattern assistive tech expects, every item stays in the tab order (so
// the roving tabindex and its key handler are gone), and a strip keeps every section visible
// on a phone where the <select> hid them behind a control.
import { useLayoutEffect, useRef, useState, type CSSProperties, type ReactNode } from "react";
import { useEmbedTokens } from "../theme";
import { useContainerWidth } from "../use-container-width";

/** Container width at or above which the vertical list replaces the strip. */
export const SECTION_NAV_BREAKPOINT_PX = 720;

export interface SectionNavItem { id: string; label: ReactNode; }

interface Props {
  items: SectionNavItem[];
  value: string;
  onChange: (id: string) => void;
  "aria-label"?: string;
  /**
   * Override the list-vs-strip decision instead of self-measuring this
   * component's own wrapper against SECTION_NAV_BREAKPOINT_PX.
   *
   * Added for a 2026-09-29 production defect: AccountCenter.tsx's shell
   * puts this nav in a narrow SIDEBAR COLUMN of a two-column flex layout, so
   * self-measuring that column's own width always read "narrow" regardless of
   * how much room the account area actually had. A caller in that shape
   * measures the width that actually determines the layout (the shell that
   * would otherwise wrap the nav above the content) and passes the decision
   * down here. Leave undefined for a standalone caller.
   */
  layout?: "list" | "strip";
}

/**
 * Account section navigation: a vertical list beside the content at wide
 * container widths, a horizontal scrolling strip below the breakpoint. Width is
 * measured on the CONTAINER, never the viewport (useContainerWidth); unmeasured
 * width falls back to the list.
 *
 * `value` can legitimately name no item (the self-view ACL list shrinking out from
 * under a still-in-flight `value`): then no item is `aria-current`, and every item
 * stays tabbable, so the nav never loses its tab stop.
 */
export function EmbedSectionNav({ items, value, onChange, layout, ...rest }: Props) {
  const t = useEmbedTokens();
  const [ref, measuredWidth] = useContainerWidth();
  const listRef = useRef<HTMLUListElement | null>(null);
  const itemRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  // Id of the item showing a KEYBOARD focus ring. Inline styles have no :focus-visible, so the
  // ring rides focus events and asks the browser whether this focus is keyboard-driven.
  const [ringId, setRingId] = useState<string | null>(null);
  // width > 0: a display:none container reports a 0x0 contentRect on first
  // observation, and 0 < SECTION_NAV_BREAKPOINT_PX would misread that as narrow. Only the
  // self-measured path needs the guard; a caller-supplied `layout` is trusted as-is.
  const strip = layout !== undefined
    ? layout === "strip"
    : measuredWidth !== null && measuredWidth > 0 && measuredWidth < SECTION_NAV_BREAKPOINT_PX;

  // Centre the active item in the strip by moving the strip's own scrollLeft.
  // scrollIntoView is never used: it can scroll the HOST page.
  // ⚠️ AMENDED 2026-10-03 (account-area redesign Task 4, fix round 1). ORIGINAL: the item's
  // offset came from el.offsetLeft — measured from the nearest POSITIONED ancestor, not from
  // this strip. The embed root renders position:relative and every element between it and the
  // <ul> is static, so any horizontal inset between the root's edge and the strip (a padded
  // shell row, a host wrapper inside the mount, future layout work) mis-centred the active
  // item by exactly that inset (measured 120.88px for a 120px inset). CURRENT: rect-relative
  // arithmetic — elRect.left - listRect.left + scrollLeft is the item's offset within the
  // strip's CONTENT, independent of every ancestor's geometry.
  useLayoutEffect(() => {
    if (!strip) return;
    const list = listRef.current;
    const el = itemRefs.current[value];
    if (!list || !el) return;
    const listRect = list.getBoundingClientRect();
    const elRect = el.getBoundingClientRect();
    list.scrollLeft = elRect.left - listRect.left + list.scrollLeft - (list.clientWidth - el.offsetWidth) / 2;
  }, [strip, value]);

  const listStyle: CSSProperties = strip
    ? {
        display: "flex", flexDirection: "row", gap: t.spacing.xs, margin: 0, padding: 0, listStyle: "none",
        overflowX: "auto", scrollbarWidth: "thin", whiteSpace: "nowrap",
        borderBottom: `1px solid ${t.colors.borderSubtle}`,
      }
    : { display: "flex", flexDirection: "column", gap: 2, margin: 0, padding: 0, listStyle: "none" };

  const itemStyle = (active: boolean, ring: boolean): CSSProperties => {
    const base: CSSProperties = {
      // Task 12 check (d): a keyboard-focused item paints a 3px solid ring (inset, so the strip's
      // overflow clipping cannot cut it). ORIGINAL: the UA default (`outline: auto`, 1px computed).
      ...(ring ? { outline: `3px solid ${t.colors.borderFocus}`, outlineOffset: -3 } : {}),
      fontFamily: "inherit", fontSize: t.fontSize.sm, fontWeight: active ? t.fontWeight.medium : t.fontWeight.regular,
      border: "none", cursor: "pointer", textAlign: "left", background: "transparent",
      minHeight: t.buttonHeight, boxSizing: "border-box", display: "flex", alignItems: "center",
      padding: `${t.spacing.sm}px ${t.spacing.md}px`,
    };
    if (strip) {
      return {
        ...base, flex: "0 0 auto", width: "auto", borderRadius: 0,
        borderBottom: `2px solid ${active ? t.colors.primary : "transparent"}`,
        color: active ? t.colors.text : t.colors.textSecondary,
      };
    }
    return {
      ...base, width: "100%", borderRadius: t.borderRadiusControl,
      background: active ? t.colors.primaryTint : "transparent",
      // The 3px inline-start rail marks the active item without relying on colour alone.
      borderInlineStart: `3px solid ${active ? t.colors.primary : "transparent"}`,
      color: t.colors.text,
    };
  };

  return (
    <div ref={ref} style={{ fontFamily: t.fontFamily, minWidth: 0 }}>
      <nav aria-label={rest["aria-label"]} data-layout={strip ? "strip" : "list"}>
        <ul ref={listRef} role="list" style={listStyle}>
          {items.map((it) => {
            const active = it.id === value;
            return (
              <li key={it.id} style={{ display: "block", flex: strip ? "0 0 auto" : undefined }}>
                <button
                  type="button"
                  ref={(el) => { itemRefs.current[it.id] = el; }}
                  aria-current={active ? "page" : undefined}
                  style={itemStyle(active, ringId === it.id)}
                  onFocus={(e) => {
                    // Safari < 15.4 throws a SyntaxError on :focus-visible; fall back to the UA ring there.
                    let visible = false;
                    try { visible = e.currentTarget.matches(":focus-visible"); } catch { visible = false; }
                    if (visible) setRingId(it.id);
                  }}
                  onBlur={() => setRingId((cur) => (cur === it.id ? null : cur))}
                  onClick={() => onChange(it.id)}
                >
                  {it.label}
                </button>
              </li>
            );
          })}
        </ul>
      </nav>
    </div>
  );
}
